import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Suppliers tab of the Telegram shop admin.
 *
 * Links other sellers' APIs (ProdSeller and the like). Their products sync in
 * automatically, priced at the supplier's markup; the owner deletes what they do
 * not want (deleted stays deleted on later syncs) and sets each sell price. When
 * a buyer pays, the bot buys the item from the supplier and hands it over.
 */

const NO_DURATION = 1000;
// The supplier APIs the server understands (supplier-shop.js KINDS).
const SUPPLIER_TYPES = [
    { kind: 'prodseller', label: 'ProdSeller', base: 'https://prodseller.com/v1' },
    { kind: 'hylehub', label: 'HyleHub', base: 'https://hypervin.xyz/api' },
    { kind: 'insight', label: 'InsightXPro', base: 'https://api.insightxpro.store/api/v1' },
];
const DURATIONS = [
    [NO_DURATION, 'Standard (no duration)'], [0, 'Lifetime'],
    [-1, '1 day'], [-7, '7 days'], [-14, '14 days'], [-30, '30 days'],
    [1, '1 month'], [2, '2 months'], [3, '3 months'], [6, '6 months'],
    [12, '1 year'], [24, '2 years'], [36, '3 years']
];
const durText = m => (DURATIONS.find(d => d[0] === m) || [m, m < 0 ? `${-m} days` : `${m} months`])[1];
const money = n => (n === null || n === undefined || n === '') ? '—' : '$' + Number(n).toFixed(2);
const ago = iso => {
    if (!iso) return 'never';
    const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
    if (s < 90) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} h ago`;
    return `${Math.round(s / 86400)} d ago`;
};

function TelegramSuppliersAdmin() {
    const [list, setList] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState('');
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({ kind: '', name: '', apiKey: '', baseUrl: '', markupPct: '30', autoList: true });
    const [openId, setOpenId] = useState(null);
    const [keyEdit, setKeyEdit] = useState({ id: null, value: '' });

    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('listed');
    const [picked, setPicked] = useState(() => new Set());
    const [shown, setShown] = useState(60);
    const [priceDraft, setPriceDraft] = useState({});   // productId -> text being typed
    const [bulkValue, setBulkValue] = useState('');

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    const flash = (msg, isError = false) => {
        if (isError) { setError(msg); setNotice(null); } else { setNotice(msg); setError(null); }
        setTimeout(() => { setError(null); setNotice(null); }, 6000);
    };

    const loadSuppliers = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/suppliers');
        if (ok) {
            const rows = body.suppliers || [];
            setList(rows);
            setOpenId(cur => (cur && rows.some(s => s.id === cur)) ? cur : (rows[0] ? rows[0].id : null));
        }
        setLoaded(true);
    }, [authFetch]);

    const loadProducts = useCallback(async (id) => {
        if (!id) { setProducts([]); return; }
        const { ok, body } = await authFetch(`/admin/telegram/suppliers/${id}/products`);
        if (ok) setProducts(body.products || []);
    }, [authFetch]);

    useEffect(() => { loadSuppliers(); }, [loadSuppliers]);
    useEffect(() => { setPicked(new Set()); setShown(60); loadProducts(openId); }, [openId, loadProducts]);

    const addSupplier = async () => {
        if (!draft.apiKey.trim()) return flash('Paste the API key from the supplier.', true);
        setBusy('add');
        const { ok, body } = await authFetch('/admin/telegram/suppliers', { method: 'POST', body: JSON.stringify(draft) });
        setBusy('');
        if (!ok) return flash(body.error || 'Could not add the supplier.', true);
        const s = body.sync || {};
        flash(s.error ? `Supplier linked, but the first sync failed: ${s.error}`
            : `Supplier linked - ${s.added || 0} products imported.`, !!s.error);
        setAdding(false);
        setDraft({ ...draft, apiKey: '' });
        setOpenId(body.id);
        loadSuppliers();
    };

    const patchSupplier = async (id, patch, okMsg) => {
        const { ok, body } = await authFetch(`/admin/telegram/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
        if (!ok) return flash(body.error || 'Could not save.', true);
        if (okMsg) flash(okMsg);
        loadSuppliers();
        return true;
    };

    const syncNow = async (id) => {
        setBusy(`sync${id}`);
        const { ok, body } = await authFetch(`/admin/telegram/suppliers/${id}/sync`, { method: 'POST' });
        setBusy('');
        if (!ok) return flash(body.error || 'Sync failed.', true);
        flash(`Synced: ${body.added} new, ${body.updated} updated${body.gone ? `, ${body.gone} no longer sold by the supplier` : ''}${body.costUp ? `, ${body.costUp} cost went up` : ''}.`);
        loadSuppliers();
        if (id === openId) loadProducts(id);
    };

    const removeSupplier = async (s) => {
        if (!window.confirm(`Remove ${s.name}? Its ${s.products} products leave the bot immediately. Your balance with them is not affected.`)) return;
        const { ok, body } = await authFetch(`/admin/telegram/suppliers/${s.id}`, { method: 'DELETE' });
        if (!ok) return flash(body.error || 'Could not remove.', true);
        flash(`${s.name} removed.`);
        if (openId === s.id) setOpenId(null);
        loadSuppliers();
    };

    const saveProduct = async (p, patch) => {
        const { ok, body } = await authFetch(`/admin/telegram/supplier-products/${p.id}`, { method: 'PUT', body: JSON.stringify(patch) });
        if (!ok) { flash(body.error || 'Could not save.', true); return false; }
        setProducts(ps => ps.map(x => x.id === p.id ? body.product : x));
        loadSuppliers();
        return true;
    };

    const commitPrice = async (p) => {
        const raw = priceDraft[p.id];
        if (raw === undefined) return;
        const v = Number(raw);
        if (!(v > 0)) { flash('Enter a price above zero.', true); return; }
        if (Math.abs(v - Number(p.sellUsd)) < 0.001) { setPriceDraft(d => { const n = { ...d }; delete n[p.id]; return n; }); return; }
        if (v <= p.costUsd && !window.confirm(`$${v.toFixed(2)} is not above the supplier cost of $${p.costUsd.toFixed(2)}. The bot will NOT sell it at this price. Save anyway?`)) return;
        if (await saveProduct(p, { sellUsd: v })) setPriceDraft(d => { const n = { ...d }; delete n[p.id]; return n; });
    };

    const bulk = async (action) => {
        const ids = [...picked];
        if (!ids.length) return flash('Tick the products first.', true);
        if ((action === 'markup' || action === 'price') && !(Number(bulkValue) >= 0 && bulkValue !== '')) {
            return flash(action === 'markup' ? 'Enter a markup %.' : 'Enter a price.', true);
        }
        if (action === 'delete' && !window.confirm(`Delete ${ids.length} product(s) from the bot? They stay deleted on future syncs (you can restore them from "Deleted").`)) return;
        const { ok, body } = await authFetch(`/admin/telegram/suppliers/${openId}/products/bulk`, {
            method: 'POST', body: JSON.stringify({ ids, action, value: bulkValue })
        });
        if (!ok) return flash(body.error || 'Could not apply.', true);
        flash(`${body.changed} product(s) updated.`);
        setPicked(new Set());
        loadProducts(openId);
        loadSuppliers();
    };

    const flag = p => p.deleted ? 'deleted' : p.gone ? 'gone' : !p.listed ? 'unlisted'
        : p.belowCost ? 'belowcost' : !p.inStock ? 'soldout' : 'live';

    const tally = useMemo(() => products.reduce((a, p) => {
        const f = flag(p);
        a[f] = (a[f] || 0) + 1;
        if (!p.deleted && !p.gone && p.listed) a.listed = (a.listed || 0) + 1;
        return a;
    }, {}), [products]);

    const FILTERS = [
        { key: 'listed', label: 'In the bot', match: p => !p.deleted && !p.gone && p.listed, n: tally.listed },
        { key: 'live', label: 'Selling now', match: p => flag(p) === 'live', n: tally.live },
        { key: 'unlisted', label: 'Not listed', match: p => flag(p) === 'unlisted', n: tally.unlisted },
        { key: 'belowcost', label: 'Price ≤ cost', match: p => flag(p) === 'belowcost', n: tally.belowcost },
        { key: 'soldout', label: 'Out of stock', match: p => flag(p) === 'soldout', n: tally.soldout },
        { key: 'deleted', label: 'Deleted', match: p => p.deleted, n: tally.deleted },
        { key: 'gone', label: 'Removed by supplier', match: p => p.gone && !p.deleted, n: tally.gone },
        { key: 'all', label: 'All', match: () => true, n: products.length },
    ];
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const activeFilter = FILTERS.find(f => f.key === filter) || FILTERS[0];
    const visible = products.filter(p => activeFilter.match(p) && words.every(w => p.name.toLowerCase().includes(w)));
    const open = list.find(s => s.id === openId);
    const allPicked = visible.length > 0 && visible.slice(0, shown).every(p => picked.has(p.id));

    const togglePick = id => setPicked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const pickAllShown = () => setPicked(prev => {
        const n = new Set(prev);
        const slice = visible.slice(0, shown);
        if (slice.every(p => n.has(p.id))) slice.forEach(p => n.delete(p.id)); else slice.forEach(p => n.add(p.id));
        return n;
    });

    const statusTag = p => {
        const f = flag(p);
        const map = {
            live: ['Selling', '#16a34a'], unlisted: ['Not listed', '#6b7280'], deleted: ['Deleted', '#dc2626'],
            gone: ['Removed by supplier', '#9ca3af'], belowcost: ['Price ≤ cost', '#d97706'], soldout: ['Out of stock', '#dc2626']
        };
        const [label, color] = map[f];
        return <span className="tg-tag" style={{ color, borderColor: color }}>{label}</span>;
    };

    return (
        <div>
            {notice && <div className="tg-note ok">{notice}</div>}
            {error && <div className="tg-note bad">{error}</div>}

            <div className="tg-card">
                <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <span>Suppliers</span>
                    {!adding && <button className="tg-btn primary sm" onClick={() => setAdding(true)}>+ Link a supplier API</button>}
                </h3>
                <p className="tg-sub">
                    Resell other sellers' products through your bot. Their catalogue syncs every 15 minutes; new products
                    are added at your markup. Delete what you don't want — deleted items stay deleted. When a buyer pays,
                    the bot buys the item from the supplier with your balance there and delivers it automatically.
                </p>

                {adding && (
                    <div className="tg-card" style={{ marginTop: 14 }}>
                        <div className="tg-grid">
                            <label>Supplier type
                                <select className="tg-input" value={draft.kind || ''} onChange={e => {
                                    const kind = e.target.value;
                                    const preset = SUPPLIER_TYPES.find(t => t.kind === kind);
                                    setDraft({ ...draft, kind, ...(preset ? { baseUrl: preset.base, name: draft.name && !SUPPLIER_TYPES.some(t => t.label === draft.name) ? draft.name : preset.label } : {}) });
                                }}>
                                    <option value="">Detect automatically</option>
                                    {SUPPLIER_TYPES.map(t => <option key={t.kind} value={t.kind}>{t.label}</option>)}
                                </select>
                            </label>
                            <label>Name (only you see this)
                                <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="ProdSeller" />
                            </label>
                        </div>
                        <label>API key
                            <input value={draft.apiKey} onChange={e => setDraft({ ...draft, apiKey: e.target.value })}
                                placeholder="psk_… (ProdSeller)  ·  sk_… (HyleHub)  ·  isk_live_… (InsightXPro)" autoComplete="off" />
                        </label>
                        <div className="tg-grid">
                            <label>API address
                                <input value={draft.baseUrl} onChange={e => setDraft({ ...draft, baseUrl: e.target.value })}
                                    placeholder="Leave empty to use the standard address for this type" />
                            </label>
                            <label>Default markup %
                                <input type="number" min="0" step="1" value={draft.markupPct} onChange={e => setDraft({ ...draft, markupPct: e.target.value })} />
                            </label>
                        </div>
                        <div className="tg-flags" style={{ marginBottom: 12 }}>
                            <label>
                                <input type="checkbox" checked={draft.autoList} onChange={e => setDraft({ ...draft, autoList: e.target.checked })} />
                                {' '}Put new products in the bot automatically
                                <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                    Untick to review each new product first — they arrive as "Not listed".
                                </span>
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className="tg-btn primary" disabled={busy === 'add'} onClick={addSupplier}>
                                {busy === 'add' ? 'Checking key and importing…' : 'Link and import products'}
                            </button>
                            <button className="tg-btn" onClick={() => setAdding(false)}>Cancel</button>
                        </div>
                    </div>
                )}

                {loaded && !list.length && !adding && <div className="tg-empty">No suppliers linked yet.</div>}

                {list.map(s => (
                    <div key={s.id} className="tg-row" style={{ cursor: 'pointer', outline: s.id === openId ? '2px solid var(--primary-color, #2563eb)' : 'none', borderRadius: 10, padding: 10, marginTop: 10 }}
                        onClick={() => setOpenId(s.id)}>
                        <div className="tg-row-main">
                            <div className="tg-row-title">
                                {s.name} {s.kindLabel && <span className="tg-tag" style={{ color: '#2563eb' }}>{s.kindLabel}</span>}
                                {' '}{!s.enabled && <span className="tg-tag" style={{ color: '#6b7280' }}>Disabled</span>}
                                {s.needsCheck > 0 && <span className="tg-tag" style={{ color: '#dc2626' }} title="The supplier gave no clear answer; this supplier cannot be retried safely. Check its order history, then Fulfil or Refund in Orders.">{s.needsCheck} to check by hand</span>}
                                {s.pendingOrders > 0 && <span className="tg-tag" style={{ color: '#d97706' }}>{s.pendingOrders} delivering</span>}
                            </div>
                            <div className="tg-row-meta">
                                Balance <b>{money(s.balanceUsd)}</b>{s.account ? ` · ${s.account}` : ''} · {s.listed} in the bot of {s.products}
                                {s.deleted ? ` · ${s.deleted} deleted` : ''} · markup {s.markupPct}% · synced {ago(s.lastSyncAt)} · key {s.apiKeyMasked}
                            </div>
                            {s.lastSyncError && <div className="tg-row-meta" style={{ color: '#dc2626' }}>Last sync failed: {s.lastSyncError}</div>}
                            {keyEdit.id === s.id && (
                                <div style={{ display: 'flex', gap: 6, marginTop: 8 }} onClick={e => e.stopPropagation()}>
                                    <input className="tg-input" style={{ flex: 1 }} value={keyEdit.value} placeholder="New API key"
                                        onChange={e => setKeyEdit({ id: s.id, value: e.target.value })} />
                                    <button className="tg-btn primary sm" onClick={async () => {
                                        if (await patchSupplier(s.id, { apiKey: keyEdit.value }, 'API key replaced.')) setKeyEdit({ id: null, value: '' });
                                    }}>Save key</button>
                                    <button className="tg-btn sm" onClick={() => setKeyEdit({ id: null, value: '' })}>Cancel</button>
                                </div>
                            )}
                        </div>
                        <div className="tg-row-actions" onClick={e => e.stopPropagation()}>
                            <button className="tg-btn primary sm" disabled={busy === `sync${s.id}`} onClick={() => syncNow(s.id)}>
                                {busy === `sync${s.id}` ? 'Syncing…' : 'Sync now'}
                            </button>
                            <button className="tg-btn sm" onClick={() => {
                                const v = window.prompt('Default markup % for NEW products from this supplier (existing prices are not changed):', s.markupPct);
                                if (v !== null) patchSupplier(s.id, { markupPct: v }, 'Markup saved.');
                            }}>Markup</button>
                            <button className="tg-btn sm" onClick={() => patchSupplier(s.id, { autoList: !s.autoList }, s.autoList ? 'New products will wait for your review.' : 'New products go into the bot automatically.')}>
                                {s.autoList ? 'Auto-list: on' : 'Auto-list: off'}
                            </button>
                            <button className="tg-btn sm" onClick={() => patchSupplier(s.id, { enabled: !s.enabled }, s.enabled ? `${s.name} paused - its products left the bot.` : `${s.name} enabled.`)}>
                                {s.enabled ? 'Pause' : 'Enable'}
                            </button>
                            <button className="tg-btn sm" onClick={() => setKeyEdit({ id: s.id, value: '' })}>Change key</button>
                            <button className="tg-btn sm" onClick={() => removeSupplier(s)}>Remove</button>
                        </div>
                    </div>
                ))}
            </div>

            {open && (
                <div className="tg-card">
                    <h3>{open.name} — products</h3>
                    <p className="tg-sub">
                        <b>Cost</b> is what the supplier charges you; <b>Sell</b> is what your buyers pay. A product whose sell price
                        is not above its cost is never offered. Duration is read from the product name — fix it here if it's wrong.
                    </p>

                    <div className="tg-toolbar">
                        <input className="tg-search" value={search} onChange={e => { setSearch(e.target.value); setShown(60); }}
                            placeholder={`Search ${products.length} products…`} />
                        <div className="tg-filters">
                            {FILTERS.map(f => (
                                <button key={f.key} className={`tg-chip ${filter === f.key ? 'active' : ''}`}
                                    onClick={() => { setFilter(f.key); setShown(60); setPicked(new Set()); }}>
                                    {f.label} <span className="tg-chip-n">{f.n || 0}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="tg-bulkbar" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '10px 0' }}>
                        <span><b>{picked.size}</b> selected</span>
                        <button className="tg-btn sm" onClick={() => bulk('delete')}>Delete</button>
                        <button className="tg-btn sm" onClick={() => bulk('restore')}>Restore</button>
                        <button className="tg-btn sm" onClick={() => bulk('list')}>Put in bot</button>
                        <button className="tg-btn sm" onClick={() => bulk('unlist')}>Take out of bot</button>
                        <input className="tg-input" style={{ width: 90 }} type="number" min="0" step="0.01" value={bulkValue}
                            onChange={e => setBulkValue(e.target.value)} placeholder="value" />
                        <button className="tg-btn sm" onClick={() => bulk('markup')}>Set markup %</button>
                        <button className="tg-btn sm" onClick={() => bulk('price')}>Set price $</button>
                    </div>

                    {!visible.length ? <div className="tg-empty">Nothing here.</div> : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="tg-table">
                                <thead>
                                    <tr>
                                        <th><input type="checkbox" checked={allPicked} onChange={pickAllShown} title="Select all shown" /></th>
                                        <th>Product</th><th>Cost</th><th>Sell</th><th>Profit</th><th>Stock</th><th>Duration</th><th>Status</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.slice(0, shown).map(p => {
                                        const typed = priceDraft[p.id];
                                        const sell = typed !== undefined ? Number(typed) : p.sellUsd;
                                        const profit = sell > 0 ? sell - p.costUsd : null;
                                        return (
                                            <tr key={p.id} style={{ opacity: p.deleted || p.gone ? 0.55 : 1 }}>
                                                <td><input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)} /></td>
                                                <td style={{ minWidth: 220 }}>
                                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                        {p.imageUrl && <img src={p.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer"
                                                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                                                            onError={e => { e.currentTarget.style.display = 'none'; }} />}
                                                        <div>
                                                            <div style={{ fontWeight: 600 }}>{p.name}</div>
                                                            <div style={{ fontSize: 11, opacity: 0.7 }}>
                                                                {p.requiresEmail ? 'Activated on buyer email' : p.deliveryType === 'instant' ? 'Instant delivery' : `Delivery: ${p.deliveryType}`}
                                                                {p.publicPriceUsd ? ` · list price ${money(p.publicPriceUsd)}` : ''}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{money(p.costUsd)}</td>
                                                <td>
                                                    <input className="tg-input" style={{ width: 82 }} type="number" min="0" step="0.01"
                                                        value={typed !== undefined ? typed : (p.sellUsd ?? '')}
                                                        onChange={e => setPriceDraft(d => ({ ...d, [p.id]: e.target.value }))}
                                                        onBlur={() => commitPrice(p)}
                                                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
                                                </td>
                                                <td style={{ color: profit !== null && profit <= 0 ? '#dc2626' : undefined, fontWeight: 600 }}>
                                                    {profit === null ? '—' : `${profit < 0 ? '-' : ''}$${Math.abs(profit).toFixed(2)}`}
                                                </td>
                                                <td>{!p.inStock ? '0' : (p.stock > 0 ? p.stock : 'yes')}</td>
                                                <td>
                                                    <select className="tg-input" value={p.months} onChange={e => saveProduct(p, { months: Number(e.target.value) })}>
                                                        {!DURATIONS.some(d => d[0] === p.months) && <option value={p.months}>{durText(p.months)}</option>}
                                                        {DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                                    </select>
                                                </td>
                                                <td>{statusTag(p)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {p.deleted
                                                        ? <button className="tg-btn sm" onClick={() => saveProduct(p, { deleted: false, listed: true })}>Restore</button>
                                                        : <>
                                                            <button className="tg-btn sm" onClick={() => saveProduct(p, { listed: !p.listed })}>{p.listed ? 'Take out' : 'Put in bot'}</button>{' '}
                                                            <button className="tg-btn sm" onClick={() => saveProduct(p, { deleted: true })}>Delete</button>
                                                        </>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {visible.length > shown && (
                        <div style={{ textAlign: 'center', marginTop: 10 }}>
                            <button className="tg-btn" onClick={() => setShown(n => n + 60)}>Show more ({visible.length - shown} left)</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TelegramSuppliersAdmin;
